import { useState } from 'react'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { ResultState } from '@/console/ui/ResultState'
import { RowList, RowListRow } from '@/console/ui/RowList'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Stack } from '@/console/ui/Stack'
import { Tag } from '@/console/ui/Tag'
import {
  MAX_REDISPATCH_IDS,
  classifyRedispatchResult,
  describeRedispatchResult,
  describeRedispatchWait,
  parseStandardOrderIds,
  redispatchOutcomeLabel,
} from './redispatch'
import { RedispatchController } from './useManualRedispatch'

/**
 * LOGISPRD-12506 — F-OPT `재발사`(`s-fopt-redispatch`) + `재발사 결과`(`s-fopt-result`) 두 섹션을
 * 한 패널에 묶는다. 시안의 재발사 대상 선택 UI(필터·상태칩·체크박스 표)는 1차 범위에서 빠졌고,
 * 대신 `standard_order.id` 직접 입력(textarea)으로 대체됐다 — 브리프가 시안보다 우선한다.
 *
 * Task 14 — `data/redispatch.ts` 의 `requestManualRedispatch` 가 실제 `POST
 * /workflow/outbound-dispatch/manual` 을 부른다(동기·최대 5분). 이 컴포넌트의 잠금 로직은 1단계와
 * 동일하다 — `submitting` 이 응답(성공이든 실패든)이 돌아올 때까지 `true` 로 남아 입력·체크박스·
 * 버튼을 계속 막는다. axios 자동 재시도는 어디에도 추가하지 않는다 — 실패하면 `submitError` 로
 * 사용자에게 보여줄 뿐, 이 함수가 스스로 다시 부르지 않는다(재시도는 오직 사용자가 폼을 다시
 * 제출했을 때만 — 실제 주문 중복 재발사를 막기 위한 핵심 제약).
 *
 * LOGISPRD-13034 — 제출 상태(대기·잠금·결과·에러)를 `useManualRedispatch` 로 끌어올렸다. F-OPT
 * 조회 패널(Task 6)이 같은 컨트롤러를 공유해 `controller.ask(ids, 'search')` 로 이 패널을 거치지
 * 않고도 같은 확인 다이얼로그·같은 잠금·같은 결과 섹션을 타게 한다.
 */
export type RedispatchPanelProps = {
  /**
   * PR 코멘트(LOGISPRD-13034, H3) — 프로덕션 호출부는 `FoptTab` 하나뿐이고 항상 넘긴다. 예전에는
   * 생략하면 `own = useManualRedispatch()` 로 자체 상태를 만들었는데, 그 분기는 실제 실행 경로에서
   * 한 번도 읽히지 않았고, 나중에 다른 화면이 실수로 빠뜨려도 타입 오류 없이 "잠금이 갈린 패널"이
   * 조용히 렌더되는 위험만 남겼다 — 그래서 필수로 바꾼다.
   *
   * 호출부는 컴포넌트 생명주기 동안 같은 컨트롤러 인스턴스를 계속 넘겨야 한다 — 도중에 다른
   * 인스턴스로 바꾸면 잠금이 둘로 쪼개진다(진행 중이던 제출은 옛 인스턴스에, 이후 조작은 새
   * 인스턴스에 걸려 `submitting` 이 서로 다르게 보일 수 있다).
   */
  controller: RedispatchController
}

export const RedispatchPanel = ({ controller }: RedispatchPanelProps): JSX.Element => {
  const c = controller

  const [raw, setRaw] = useState('')

  const parsed = parseStandardOrderIds(raw)
  // PR 리뷰 — `invalid` 가 하나라도 있으면 제출을 막는다. 예전에는 경고만 띄우고 유효한 값만
  // 골라 보냈는데, "1, 주문번호" 처럼 섞여 들어오면 온콜 담당자는 둘 다 재발사됐다고 믿지만
  // 실제로는 1건만 나갔다. 실제 주문을 다시 쏘는 조치라 부분 실행이 침묵하는 쪽이 더 위험하다.
  const canSubmit = parsed.ids.length > 0 && parsed.invalid.length === 0 && !parsed.overLimit && !c.submitting

  const resultsState = c.resultsState

  const resultRows: RowListRow[] =
    resultsState.status === 'ready'
      ? resultsState.data.map((r) => {
          const outcome = classifyRedispatchResult(r)
          const meta = redispatchOutcomeLabel(outcome)
          return {
            key: String(r.standardOrderId),
            name: r.standardOrderId,
            mono: true,
            desc: describeRedispatchResult(r),
            right: <Tag tone={meta.tone}>{meta.label}</Tag>,
          }
        })
      : []

  return (
    <>
      <SectionHeading
        id="s-fopt-redispatch"
        level={2}
        title="재발사"
        badge={<SourceBadge tone="have">기존 · POST /workflow/outbound-dispatch/manual</SourceBadge>}
      />

      <Stack>
        <Field label="standard_order.id — 주문번호 아님 (쉼표 · 줄바꿈)">
          <textarea
            rows={4}
            placeholder="8842140, 8842188"
            spellCheck={false}
            value={raw}
            disabled={c.submitting}
            onChange={(e) => setRaw(e.target.value)}
          />
        </Field>

        <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
          유효 {parsed.ids.length}건 · 중복 제외 {parsed.duplicates}건 · 최대 {MAX_REDISPATCH_IDS}건
        </div>

        {parsed.overLimit ? <Callout tone="crit">한 번에 최대 {MAX_REDISPATCH_IDS}건까지 보낼 수 있어요.</Callout> : null}
        {parsed.invalid.length > 0 ? <Callout tone="warn">숫자가 아닌 값이 있어요: {parsed.invalid.join(', ')}</Callout> : null}

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, color: 'var(--ink-2)', cursor: 'pointer' }}>
          <input type="checkbox" checked={c.wakeUp} disabled={c.submitting} onChange={(e) => c.setWakeUp(e.target.checked)} />
          <span>즉시 처리 (wakeUp)</span>
        </label>

        <div>
          <Button disabled={!canSubmit} onClick={() => c.ask(parsed.ids, 'manual')}>
            {c.submitting ? '재발사 중…' : '재발사'}
          </Button>
        </div>
      </Stack>

      <Note>
        입력은 주문번호가 아니라 <b>standard_order.id</b>(Long) 예요. 온콜 중에 주문번호를 붙여넣지 않도록 주의해요.
      </Note>

      <SectionHeading id="s-fopt-result" level={2} title="재발사 결과" />
      {c.progress && c.progress.batches > 1 ? (
        <Callout>
          {c.progress.batches}번 중 {c.progress.batch}번째 보내는 중 · {c.progress.done}/{c.progress.total}건 응답받음
        </Callout>
      ) : null}
      {c.halted ? (
        <Callout tone="crit">
          {c.halted.batch}번째 묶음이 실패해 멈췄어요: {c.halted.message} · 보내지 못한 건 {c.halted.unsent}건이에요.
        </Callout>
      ) : null}
      <ResultState state={resultsState}>
        <RowList rows={resultRows} />
      </ResultState>
      <Note>
        200 ≠ 전건 성공. <b>accepted</b> 만 보지 말고 <b>routedToOptimizer</b> 를 봐요. 자동 재시도는 하지 않아요.
      </Note>

      <ConfirmDialog
        open={c.pending !== null}
        title="재발사할까요?"
        body={
          c.pending?.source === 'search' ? (
            <>
              <p style={{ margin: 0 }}>조회에서 고른 {c.pending.ids.length}건을 재발사합니다. {describeRedispatchWait(c.pending.ids.length)}, 그동안 화면이 잠깁니다.</p>
              <p
                style={{
                  margin: '10px 0',
                  fontFamily: 'var(--mono)',
                  fontSize: 12.5,
                  color: 'var(--ink-3)',
                  background: 'var(--surface)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  wordBreak: 'break-all',
                }}
              >
                {c.pending.ids.join(', ')}
              </p>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, color: 'var(--ink-2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={c.wakeUp} onChange={(e) => c.setWakeUp(e.target.checked)} />
                <span>즉시 처리 (wakeUp)</span>
              </label>
            </>
          ) : (
            `${c.pending?.ids.length ?? 0}건을 재발사합니다. wakeUp=${c.wakeUp ? 'true' : 'false'}. ${describeRedispatchWait(c.pending?.ids.length ?? 0)}, 그동안 화면이 잠깁니다.`
          )
        }
        confirmLabel="재발사"
        onConfirm={() => {
          // pending 은 confirm() 이 부르면서 비운다 — 소스 판정은 그 전에 떠 둔다.
          const fromSearch = c.pending?.source === 'search'
          c.confirm()
          if (fromSearch) document.getElementById('s-fopt-result')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
        }}
        onCancel={c.cancel}
      />
    </>
  )
}
